import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import TableV2 from "../../../../../components/TableV2";
import TitleButtons from "../../../../../components/TitleButtons";
import TableActions from "../../../../../components/TableActions";

import EmpresaClienteSidePanel from "./EmpresaClienteSidePanel";

import {
  formatCNPJ,
  useEmpresasCliente,
  type ClienteAPI,
} from "./empresasCadastradasData";

type Row = React.ReactNode[];

function onlyDigits(value: unknown): string {
  return String(value ?? "").replace(/\D+/g, "");
}

function getResponsaveisNomes(cliente: ClienteAPI): string[] {
  /*
   * Prioriza a estrutura completa, porque ela contém os IDs
   * necessários para adicionar ou remover coordenadores.
   */
  if (Array.isArray(cliente.responsaveis) && cliente.responsaveis.length > 0) {
    return cliente.responsaveis
      .map((responsavel) => String(responsavel?.nome ?? "").trim())
      .filter(Boolean);
  }

  /*
   * Compatibilidade com a resposta antiga da API,
   * que retorna apenas responsaveis_nomes.
   */
  if (Array.isArray(cliente.responsaveis_nomes)) {
    return cliente.responsaveis_nomes
      .map((nome) => String(nome ?? "").trim())
      .filter(Boolean);
  }

  return [];
}

function formatResponsaveis(cliente: ClienteAPI): string {
  const nomes = getResponsaveisNomes(cliente);

  return nomes.length > 0 ? nomes.join(", ") : "—";
}

const EmpresasCadastradas: React.FC = () => {
  const navigate = useNavigate();

  const { data, loading, refresh } = useEmpresasCliente();

  /*
   * Guardamos apenas o CNPJ da empresa selecionada.
   *
   * Dessa forma, quando o refresh atualizar a lista,
   * o painel passa a receber automaticamente os dados
   * mais recentes da empresa.
   */
  const [selectedCnpj, setSelectedCnpj] = useState<string | null>(null);

  const headers = useMemo(
    () => [
      "CNPJ",
      "Razão Social",
      "Nome Fantasia",
      "Coordenadores Responsáveis",
    ],
    []
  );

  const columnWidths = useMemo(() => ["180px", "360px", "300px", "320px"], []);

  const rows: Row[] = useMemo(() => {
    return data.map((cliente) => [
      formatCNPJ(cliente.cnpj),

      cliente.razao_social?.trim() || "—",

      cliente.nome_fantasia?.trim() || "—",

      formatResponsaveis(cliente),
    ]);
  }, [data]);

  /*
   * Localiza novamente a empresa dentro dos dados atualizados.
   *
   * Não armazenamos diretamente o objeto inteiro no estado
   * para evitar que o painel continue exibindo dados antigos
   * depois de adicionar ou remover um coordenador.
   */
  const selectedCliente = useMemo<ClienteAPI | null>(() => {
    if (!selectedCnpj) {
      return null;
    }

    return (
      data.find((cliente) => onlyDigits(cliente.cnpj) === selectedCnpj) ?? null
    );
  }, [data, selectedCnpj]);

  function handleRowDoubleClick(rowIndex: number) {
    const cliente = data[rowIndex];

    if (!cliente) {
      return;
    }

    const cnpj = onlyDigits(cliente.cnpj);

    if (!cnpj) {
      return;
    }

    setSelectedCnpj(cnpj);
  }

  function handleCloseSidePanel() {
    setSelectedCnpj(null);
  }

  async function handleRefresh() {
    await refresh();
  }

  return (
    <div>
      <TitleButtons
        fullBleedLine
        title="Recursos Humanos - Empresas/clientes"
      />

      <TableActions
        buttons={[
          {
            label: "Registrar Empresa",

            onClick: () =>
              navigate(
                "/recursos-humanos/empresas-cadastradas/registrar-empresa"
              ),

            variant: "primary",

            textColor: "#1C1B1B",
          },
        ]}
      />

      <TableV2
        headers={headers}
        rows={rows}
        columnWidths={columnWidths}
        onDoubleClick={handleRowDoubleClick}
        responsiveWidths={{
          /*
           * Índices:
           *
           * 0 = CNPJ
           * 1 = Razão Social
           * 2 = Nome Fantasia
           * 3 = Coordenadores Responsáveis
           */

          1300: {
            1: "300px",
            2: "240px",
            3: "280px",
          },

          1000: {
            0: "160px",
            1: "240px",

            /*
             * Oculta Nome Fantasia em telas menores.
             */
            2: "0px",

            /*
             * Mantém coordenadores visíveis.
             */
            3: "260px",
          },
        }}
      />

      <EmpresaClienteSidePanel
        open={!loading && selectedCliente !== null}
        cliente={selectedCliente}
        refresh={handleRefresh}
        onClose={handleCloseSidePanel}
      />
    </div>
  );
};

export default EmpresasCadastradas;
